import {
  animateStreamLainDropdownPanelClose,
  decorateStreamLainDropdownPanel,
  getStreamLainDropdownMenuChromeCss,
  STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
} from '@affine/core/components/dropdown-panel-chrome/chrome';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import type { NotificationService } from '@blocksuite/affine/shared/services';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { streamLainDisclosurePopoverShellStyles } from '@blocksuite/affine-shared/utils';
import {
  ArrowDownSmallIcon,
  PinedIcon,
  PinIcon,
  PlusIcon,
} from '@blocksuite/icons/lit';
import { css, html } from 'lit';
import { property, query } from 'lit/decorators.js';

import { streamLainDropdownMenuMiddleware } from '../ai-chat-input/dropdown-menu-chrome';
import type { ChatStatus } from '../ai-chat-messages';

function renderHistoryPortalStyles() {
  return html`<style>
    ${getStreamLainDropdownMenuChromeCss()}
      ${streamLainDisclosurePopoverShellStyles}
      .ai-chat-history-popover {
      --streamlain-disclosure-overlay-width: 360px;
      width: min(360px, calc(100vw - 32px));
    }
  </style>`;
}

export class AIChatToolbar extends WithDisposable(ShadowlessElement) {
  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor status!: ChatStatus;

  @property({ attribute: false })
  accessor onNewSession!: () => void;

  @property({ attribute: false })
  accessor onTogglePin!: () => Promise<void>;

  @property({ attribute: false })
  accessor onOpenSession!: (sessionId: string) => void;

  @property({ attribute: false })
  accessor onSessionDelete!: (
    session: BlockSuitePresets.AIRecentSession
  ) => void;

  @property({ attribute: false })
  accessor onSessionRename!:
    | ((options: { sessionId: string; title: string }) => Promise<void>)
    | undefined;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @query('.history-button')
  accessor historyButton!: HTMLDivElement;

  private abortController: AbortController | null = null;
  private historyClosing = false;
  private historyPortal: HTMLElement | null = null;

  get isGenerating() {
    return this.status === 'transmitting' || this.status === 'loading';
  }

  static override styles = css`
    .ai-chat-toolbar {
      display: flex;
      gap: 8px;
      align-items: center;

      .chat-toolbar-icon {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 24px;
        height: 24px;
        border-radius: 4px;
        &:hover {
          background-color: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
        }

        svg {
          width: 16px;
          height: 16px;
          color: ${unsafeCSSVarV2('icon/primary')};
        }

        &[data-disabled='true'] {
          cursor: not-allowed;
        }

        affine-tooltip {
          white-space: nowrap;
          max-width: none;
        }
      }
    }
  `;

  override render() {
    const pinned = this.session?.pinned;
    return html`
      <div class="ai-chat-toolbar">
        <div
          class="chat-toolbar-icon"
          @click=${this.onPlusClick}
          data-testid="ai-panel-new-chat"
        >
          ${PlusIcon()}
          <affine-tooltip>
            ${I18n.t('com.streamlain.ai.chat.toolbar.new-chat')}
          </affine-tooltip>
        </div>
        <div
          class="chat-toolbar-icon"
          @click=${this.onPinClick}
          data-pinned=${!!pinned}
          data-disabled=${this.isGenerating}
          data-testid="ai-panel-pin-chat"
        >
          ${pinned ? PinedIcon() : PinIcon()}
          <affine-tooltip>
            ${pinned
              ? I18n.t('com.streamlain.ai.chat.toolbar.unpin-chat')
              : I18n.t('com.streamlain.ai.chat.toolbar.pin-chat')}
          </affine-tooltip>
        </div>
        <div
          class="chat-toolbar-icon history-button"
          @click=${this.toggleHistoryMenu}
        >
          ${ArrowDownSmallIcon()}
          <affine-tooltip .autoShift=${true}>
            ${I18n.t('com.streamlain.ai.chat.history.tooltip')}
          </affine-tooltip>
        </div>
      </div>
    `;
  }

  private readonly onPinClick = async () => {
    if (this.isGenerating) {
      this.notificationService.toast(
        I18n.t('com.streamlain.ai.chat.toolbar.pin-generating-blocked')
      );
      return;
    }
    await this.onTogglePin();
  };

  private readonly unpinConfirm = async () => {
    if (this.session && this.session.pinned) {
      try {
        const confirm = await this.notificationService.confirm({
          title: I18n.t('com.streamlain.ai.chat.toolbar.switch-pinned-title'),
          message: I18n.t(
            'com.streamlain.ai.chat.toolbar.switch-pinned-message'
          ),
          confirmText: I18n.t(
            'com.streamlain.ai.chat.toolbar.switch-pinned-confirm'
          ),
          cancelText: I18n.t('com.streamlain.ai.chat.toolbar.cancel'),
        });
        if (!confirm) {
          return false;
        }
        await this.onTogglePin();
      } catch {
        this.notificationService.toast(
          I18n.t('com.streamlain.ai.chat.toolbar.unpin-failed')
        );
      }
    }
    return true;
  };

  private readonly onPlusClick = async () => {
    const confirm = await this.unpinConfirm();
    if (confirm) {
      this.onNewSession();
    }
  };

  private readonly onSessionClick = async (sessionId: string) => {
    if (this.session?.sessionId === sessionId) {
      this.notificationService.toast(
        I18n.t('com.streamlain.ai.chat.toolbar.already-active')
      );
      return;
    }
    const confirm = await this.unpinConfirm();
    if (confirm) {
      this.onOpenSession(sessionId);
    }
  };

  private getHistoryPortalPanel() {
    if (!this.historyPortal) return null;
    if (this.historyPortal.matches('.ai-chat-history-popover')) {
      return this.historyPortal;
    }
    return this.historyPortal.querySelector<HTMLElement>(
      '.ai-chat-history-popover'
    );
  }

  private decorateHistoryPortal(attempt = 0) {
    const panel = this.getHistoryPortalPanel();
    if (!panel) {
      if (attempt < 8) {
        requestAnimationFrame(() => this.decorateHistoryPortal(attempt + 1));
      }
      return;
    }
    decorateStreamLainDropdownPanel(panel, this.historyButton);
  }

  private installHistoryPortalDismissal(abortController: AbortController) {
    const signal = abortController.signal;
    setTimeout(() => {
      document.addEventListener(
        'click',
        event => {
          const target = event.target as Node | null;
          if (!target) return;
          if (this.historyButton.contains(target)) return;
          if (this.historyPortal?.contains(target)) return;
          this.closeHistoryMenu();
        },
        { signal }
      );
    });
    document.addEventListener(
      'keydown',
      event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.closeHistoryMenu();
      },
      { signal }
    );
  }

  private readonly toggleHistoryMenu = () => {
    if (this.abortController) {
      this.closeHistoryMenu();
      return;
    }

    this.abortController = new AbortController();
    this.abortController.signal.addEventListener('abort', () => {
      this.abortController = null;
      this.historyClosing = false;
      this.historyPortal = null;
    });

    const { portal } = createLitPortal({
      template: html`${renderHistoryPortalStyles()}
        <div
          class="ai-chat-history-popover streamlain-disclosure-popover-shell"
        >
          <ai-session-history
            .session=${this.session}
            .workspaceId=${this.workspaceId}
            .onSessionClick=${this.onSessionClick}
            .onSessionDelete=${this.onSessionDelete}
            .onSessionRename=${this.onSessionRename}
          ></ai-session-history>
        </div> `,
      portalStyles: {
        zIndex: STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
      },
      container: document.body,
      shadowDom: false,
      computePosition: {
        referenceElement: this.historyButton,
        placement: 'bottom-end',
        middleware: streamLainDropdownMenuMiddleware(),
        autoUpdate: true,
      },
      abortController: this.abortController,
    });
    this.historyPortal = portal;
    this.decorateHistoryPortal();
    this.installHistoryPortalDismissal(this.abortController);
  };

  public closeHistoryMenu() {
    const abortController = this.abortController;
    if (!abortController || this.historyClosing) return;

    const panel = this.getHistoryPortalPanel();
    if (panel?.isConnected) {
      this.historyClosing = true;
      animateStreamLainDropdownPanelClose(panel, () => {
        abortController.abort();
      });
      return;
    }

    abortController.abort();
  }
}
